import React, { useState, useCallback, useEffect } from "react";
import { Box, Typography, Button, Modal, useMediaQuery } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import HeaderMain from "../../components/common/HeaderMain/HeaderMain";
import Memory from "../../components/common/Memory/Memory";
import Footer from "../../components/single/Footer/Footer";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "../../components/single/CheckoutForm/CheckoutForm";
import SignIn from "../../components/single/SignIn/SignIn";
import styles from "./Buy.module.css";
import { apiFetch } from "../../utils/api";
import * as Sentry from "@sentry/react";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

const Buy = () => {
  const { descriptor } = useParams();
  const navigate = useNavigate();

  const [memory, setMemory] = useState(null);
  const [amount, setAmount] = useState(0);
  const [clientSecret, setClientSecret] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [currentStage, setCurrentStage] = useState(1);
  const [loginOpen, setLoginOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);

  const [saleUUID, setSaleUUID] = useState(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const isSoldOut = memory?.amount > 0 && memory?.available === 0;

  const handlePaymentSuccess = useCallback(async () => {
    setCurrentStage(2);
  
    try {
      const res = await apiFetch("/memory/saleoutcome", {
        method: "POST",
        body: JSON.stringify({
          saleUUID,
          outcome: "success",
          email: userEmail,
        }),
      }, true);

      console.log(res)
  
      if (!res || res.status === "error") {
        console.error("❌ Failed to confirm sale outcome.");
      }
    } catch (err) {
      Sentry.captureException(err);
      console.error("❌ Error confirming sale outcome:", err);
    }
  }, [saleUUID, userEmail]);

  const gotoDashboard = useCallback(() => {
    navigate("/dashboard/purchased");
  }, [navigate]);

  useEffect(() => {
    const fetchMemory = async () => {
      let memoryData;
  
      try {
        const res = await apiFetch(`/api/memory/${descriptor}/details`,{}, false, true);
        memoryData = res.memory;
        setMemory(memoryData);
        setLiked(res.likedByUser);
        setLikes(res.likes);
        setAmount(parseFloat(memoryData.price));
      } catch (err) {
        Sentry.captureException(err);
        console.error("❌ Failed to load memory details:", err);
        return;
      }
  
      if ((memoryData.amount > 0 && memoryData.available > 0) || memoryData.amount==0) {
        try {
          const buyData = await apiFetch("/memory/buy", {
            method: "POST",
            body: JSON.stringify({ memory_uuid: memoryData.uuid }),
          });
    
          setClientSecret(buyData.clientSecret);
          setSaleUUID(buyData.saleUUID);
        } catch (err) {
          Sentry.captureException(err);
          console.error("❌ Failed to initiate memory purchase:", err);
        }
      }
    };

    const fetchProfile = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;
    
      try {
        const response = await apiFetch("/api/profile", { method: "GET" }, true);
    
        if (response.email) {
          setUserEmail(response.email);
          setIsAuthenticated(true);
        }
      } catch (err) {
        Sentry.captureException(err);
        console.error("❌ Failed to fetch profile info silently", err);
      }
    };

    fetchMemory();
    fetchProfile();
  }, [descriptor]);

  console.log(userEmail)

  return (
    <Box className={styles.buy}>
      <HeaderMain />

      {currentStage === 1 && memory && (
        <Box className={styles.buyStart}>
          <Typography className={styles.title}>Purchase the Memory</Typography>
          <Box className={styles.purchaseContent}>
            <Memory
              art={memory.file_path}
              description={memory.description}
              title={memory.title}
              price={`${amount.toFixed(2)}`}
              memoryDate={new Date(memory.created_at).toLocaleDateString()}
              uuid={memory.uuid}
              descriptor={memory.descriptor}
              initialLikeCount={likes}
              userLiked={liked}
              showBuyButton={false}
              all={memory.amount}
              available={memory.available}
            />
            <Box className={styles.stripeSection}>
              {isSoldOut ? (
                <Typography className={styles.paymentInfo}>❌ This memory is sold out.</Typography>
              ) : clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm
                    onPaymentSuccess={handlePaymentSuccess}
                    amount={`$${amount.toFixed(2)}`}
                    emailNeeded={!isAuthenticated}
                    userEmail={userEmail}
                    onEmailChange={(email) => {
                      console.log(email);
                      setUserEmail(email)
                    }}
                  />
                </Elements>
              ) : (
                <Typography className={styles.paymentTitle}>⏳ Loading payment options...</Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {currentStage === 2 && memory && (
        <Box className={styles.buyEnd}>
          <Typography className={styles.title}>The Moment is Yours!</Typography>
          <Box className={styles.purchaseContent}>
            <Memory
              art={memory.file_path}
              description={memory.description}
              title={memory.title}
              price={`${amount.toFixed(2)}`}
              memoryDate={new Date(memory.created_at).toLocaleDateString()}
              id={memory.id.toString()}
              descriptor={memory.descriptor}
              initialLikeCount={0}
              userLiked={false}
              showBuyButton={false}
            />

            <Box className={styles.thankYouSection}>
              <Typography className={styles.thankYouTitle}>🎉 Congratulations!</Typography>
              <Typography className={styles.messageSubtitle}>
                You now own a unique moment forever, secured and verifiable on the blockchain.
              </Typography>
              <Typography className={styles.messageDescription}>
                You can always access your purchased memories on <strong>MomentoMaster.com</strong>.
              </Typography>
              <Button
                className={styles.closeButton}
                onClick={() => {
                  if (isAuthenticated) {
                    gotoDashboard();
                  } else {
                    setLoginOpen(true);
                  }
                }}
              >
                {isAuthenticated ? "Back to Home" : "Check my moments"}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Modal open={loginOpen} onClose={() => setLoginOpen(false)}>
        <Box 
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px",
            width: isMobile ? "90%" : "800px", // Adjust width based on screen size
            maxWidth: "100%", // Prevents overflow on smaller screens
          }}>
          <SignIn
            onSignInSuccess={() => {
              setLoginOpen(false);
              navigate("/dashboard/purchased");
            }}
            emailPrefill={userEmail}
            onClose={() => setLoginOpen(false)}
          />
        </Box>
      </Modal>

      <Footer />
    </Box>
  );
};

export default Buy;